import { useRef, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";

import { Button } from "~/components";
import { icons } from "~/ui";
import { tw } from "~/utils";
import { ModalAction, ModalActions } from "./ModalActions";

interface BaseModalProps {
  children?: ReactNode;
  className?: string;
  show: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  handleConfirm?: () => void;
  closeLabel?: string;
  confirmLabel?: string;
  isLoading?: boolean;
  hideCloseButton?: boolean;
  disableConfirm?: boolean;
  onCloseAutoFocus?: (event: Event) => void;
}

interface TitleModalProps extends BaseModalProps {
  header?: never;
  titleAdornment?: ReactNode;
  actions?: ModalAction[];
  headerCloseIcon?: boolean;
  goBack?: () => void;
}

interface HeaderModalProps extends BaseModalProps {
  header: ReactNode;
  titleAdornment?: never;
  actions?: never;
  headerCloseIcon?: never;
  goBack?: never;
}

type ModalProps = TitleModalProps | HeaderModalProps;

export const Modal = ({
  children,
  className,
  show,
  title,
  header,
  titleAdornment,
  description,
  onClose,
  actions,
  handleConfirm,
  confirmLabel = "Confirm",
  isLoading = false,
  closeLabel = "Close",
  hideCloseButton = false,
  headerCloseIcon = false,
  disableConfirm,
  goBack,
  onCloseAutoFocus,
}: ModalProps) => {
  const showFooterCloseButton = !hideCloseButton && !headerCloseIcon;
  const showFooter = showFooterCloseButton || !!handleConfirm;
  const hasHeader = Boolean(header);
  const contentRef = useRef<HTMLDivElement>(null);
  const ignoreNextDismiss = useRef(false);

  return (
    <Dialog.Root
      open={show}
      onOpenChange={() => {
        if (ignoreNextDismiss.current) {
          ignoreNextDismiss.current = false;
          return;
        }

        onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="z-4 backdrop-blur-xs fixed inset-0" />
        <Dialog.Content
          ref={contentRef}
          className={tw(
            "w-165 fixed right-0 top-0 z-40 flex h-full max-w-sm flex-col gap-11 bg-white pb-10 shadow-lg !duration-1000 data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-right-1/2 data-[state=open]:slide-in-from-right-1/2 md:max-w-2xl",
            hasHeader ? "focus:outline-none" : "pt-8",
            className,
          )}
          onOpenAutoFocus={(e) => {
            if (!hasHeader) return;

            e.preventDefault();
            contentRef.current?.focus();
          }}
          onCloseAutoFocus={onCloseAutoFocus}
          onEscapeKeyDown={(e) => {
            const combobox = document.activeElement;
            if (!combobox?.matches('[role="combobox"][aria-expanded="true"]')) {
              return;
            }

            if (!e.cancelable) {
              ignoreNextDismiss.current = true;
              return;
            }

            e.preventDefault();
            combobox.dispatchEvent(
              new KeyboardEvent("keydown", { key: "Escape", bubbles: true }),
            );
          }}
          onPointerDownOutside={(e) => {
            // don't dismiss dialog when clicking inside the toast
            if (
              e.target instanceof Element &&
              e.target.closest("[data-sonner-toast]")
            ) {
              e.preventDefault();
            }
          }}
        >
          {headerCloseIcon && (
            <Dialog.Close
              aria-label="Close"
              className="text-indigo absolute right-4 top-4 rounded-full p-1 transition-opacity hover:opacity-70"
            >
              <icons.outline.XMarkIcon className="size-6" />
            </Dialog.Close>
          )}

          {hasHeader ? (
            <div className="shrink-0">
              <Dialog.Title className="sr-only">{title}</Dialog.Title>
              <Dialog.Description className="sr-only">
                {description}
              </Dialog.Description>
              {header}
            </div>
          ) : (
            <div className="flex flex-col space-y-1.5 px-8 text-left">
              <div className="mb-0 flex flex-col items-start justify-between">
                {goBack && (
                  <Button
                    variant="ghost"
                    className="text-violet flex gap-2 px-0 font-normal"
                    onClick={goBack}
                  >
                    <icons.outline.ArrowLeftCircleIcon className="text-violet size-6" />
                    Back
                  </Button>
                )}
                <div className="flex w-full items-center justify-between">
                  <Dialog.Title className="font-ivy text-indigo text-2xl font-normal leading-8 tracking-tight">
                    {title}
                  </Dialog.Title>
                  {(titleAdornment || !!actions?.length) && (
                    <div className="flex items-center gap-2">
                      {titleAdornment}
                      {!!actions?.length && <ModalActions actions={actions} />}
                    </div>
                  )}
                </div>
              </div>

              <Dialog.Description className="text-sm text-gray-400">
                {description}
              </Dialog.Description>
            </div>
          )}

          <div className="h-full overflow-auto px-8 font-sans">{children}</div>

          {showFooter && (
            <div
              className={tw(
                "gap-4 px-8",
                handleConfirm ? "grid grid-cols-2" : "flex justify-end",
              )}
            >
              {showFooterCloseButton && (
                <Dialog.Close asChild>
                  <Button
                    className="px-8"
                    variant="outline"
                    type="button"
                    aria-label={closeLabel}
                  >
                    {closeLabel}
                  </Button>
                </Dialog.Close>
              )}

              {handleConfirm && (
                <Button
                  variant="default"
                  onClick={handleConfirm}
                  disabled={isLoading || disableConfirm}
                >
                  {confirmLabel}
                </Button>
              )}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
